import { AfterViewInit, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { MatCheckboxChange } from '@angular/material/checkbox';

import {
  TreeFormControlNode,
  TreeFormControlFlatNode,
  TreeFormControlOptions,
  TreeFormControl
} from './tree-form-control';

/**
 * Tree Selector form field component
 *
 * This component provides UI to select 1,N options from a hierarchy (tree)
 * which is created by the `TreeEditorFormControlComponent` component.
 */
@Component({
    selector: 'eas-tree-selector-form-control',
    templateUrl: './tree-selector-form-control.component.html',
    styleUrls: ['./tree-selector-form-control.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TreeSelectorFormControlComponent),
            multi: true
        }],
    standalone: false
})
export class TreeSelectorFormControlComponent implements AfterViewInit, ControlValueAccessor {
  /**
   * Nodes which are received from the form rendering layers and the CouchDB
   * and are the definition of the hierarchy.
   */
  @Input() nodes: TreeFormControlNode[] = [];

  /**
   * Options for the component
   *
   * @note Not used at the moment.
   */
  @Input() options: TreeFormControlOptions = {
    allowsHierarchy: false
  }

  //
  // Material Tree infrastructure, see Material Tree documentation
  //
  public dataSource: MatTreeFlatDataSource<TreeFormControlNode, TreeFormControlFlatNode>;
  public treeControl: FlatTreeControl<TreeFormControlFlatNode>;
  public treeFlattener: MatTreeFlattener<TreeFormControlNode, TreeFormControlFlatNode>;

  /**
   * Angular form control value, this is an array of IDs
   * of the selected tree nodes.
   */
  public value: string[] = [];

  constructor() {
    this.treeControl = new FlatTreeControl<TreeFormControlFlatNode>(
      node => node.level,
      node => node.expandable,
    );

    this.treeFlattener = new MatTreeFlattener<TreeFormControlNode, TreeFormControlFlatNode>(
      TreeFormControl.nodeToFlatNode,
      node => node.level,
      node => node.expandable,
      node => node.categories,
    );

    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  ngAfterViewInit() {
    this.dataSource.data = this.nodes;
    this.treeControl.expandAll();
  }

  //
  // Tree helper methods
  //

  public hasChild(_: number, node: TreeFormControlFlatNode) {
    return node.expandable;
  }

  public isNodeChecked(node: TreeFormControlFlatNode) {
    if (this.value === null || this.value === undefined) {
      return false;
    }
    return this.value.includes(node.id);
  }

  //
  // ControlValueAccessor interface
  //

  private onChange = (_: any) => {};
  // private onTouch = () => {};

  writeValue(value: string|string[]) {
    // WTF!
    // For some really strange reason we get the value as a string
    // from the underlying layers despite the fact that in the CouchDB
    // it is stored as an array of strings. So we need to convert it
    // back to an array of strings.
    // /WTF!
    if(typeof value === 'string' && value.length === 0) {
      value = [];
    } else if (typeof value === 'string') {
      const wtf = value.replace(/[']/g, '"');
      value = JSON.parse(wtf) as string[];
    }

    this.value = value;
    this.onChange(this.value);
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(_fn: any) {
    //this.onTouch = fn;
  }

  //
  // Checkbox events handling and value management
  //

  public onNodeChange(change: MatCheckboxChange) {
    const value = [...this.value];
    if (change.checked) {
      value.push(change.source.id);
    } else {
      value.splice(value.indexOf(change.source.id), 1);
    }
    this.writeValue(value);
  }
}
